/**
 * Common validation utilities
 */

/** Max length for streamer residential address fields */
export const RESIDENTIAL_ADDRESS_MAX_LENGTH = 500

/**
 * Validate password strength according to Cognito requirements
 * @param password - The password to validate
 * @returns null if valid, error message string if invalid
 */
export function validatePassword(password: string): string | null {
  if (password.length < 10) {
    return "Password must be at least 10 characters"
  }
  if (!/[A-Z]/.test(password)) {
    return "Password must contain at least one uppercase letter"
  }
  if (!/[a-z]/.test(password)) {
    return "Password must contain at least one lowercase letter"
  }
  if (!/[0-9]/.test(password)) {
    return "Password must contain at least one number"
  }
  if (!/[^A-Za-z0-9]/.test(password)) {
    return "Password must contain at least one special character"
  }
  return null
}

/**
 * Validate email format
 * @param email - The email to validate
 * @returns true if valid, false otherwise
 */
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Validate that two passwords match
 * @param password - The first password
 * @param confirmPassword - The confirmation password
 * @returns null if they match, error message if they don't
 */
export function validatePasswordMatch(
  password: string,
  confirmPassword: string
): string | null {
  if (password !== confirmPassword) {
    return "Passwords do not match"
  }
  return null
}

/**
 * Validate a verification code (Cognito OTP)
 * Cognito sends a 6-digit numeric code.
 * @param code - The verification code to validate
 * @returns null if valid, error message string if invalid
 */
export function validateVerificationCode(code: string): string | null {
  const trimmed = code.trim()
  if (!trimmed) {
    return "Verification code is required"
  }
  if (!/^\d+$/.test(trimmed)) {
    return "Verification code must contain only numbers"
  }
  if (trimmed.length !== 6) {
    return "Verification code must be exactly 6 digits"
  }
  return null
}

/**
 * Validate a full reset-password form (verification code + new password + confirmation).
 * Runs all three checks in order and returns the first error, or null if all pass.
 */
export function validateResetPasswordForm(
  code: string,
  newPassword: string,
  confirmPassword: string
): string | null {
  return (
    validateVerificationCode(code) ??
    validatePassword(newPassword) ??
    validatePasswordMatch(newPassword, confirmPassword)
  )
}
